import React from 'react';
import { useTranslation } from 'localization';
import ProgressBarAtom, { type ProgressBarSize, type ProgressBarTone } from '../../components/atoms/ProgressBar/ProgressBarAtom';
import TagAtom, { type TagTone } from '../../components/atoms/Tag/TagAtom';
import DividerAtom from '../../components/atoms/Divider/DividerAtom';
import DateTimeDisplayAtom from '../../components/atoms/DateTimeDisplay/DateTimeDisplayAtom';
import ChipListDisplayAtom from '../../components/atoms/ChipListDisplay/ChipListDisplayAtom';
import GenericDisplayAtom from '../../components/atoms/GenericDisplay/GenericDisplayAtom';
import type { Adapter } from '../types';
import './displayAdapters.scss';

// Typed as the exact tags this map produces, NOT `keyof JSX.IntrinsicElements`: rendering
// `<Tag_>` with a union of EVERY intrinsic element makes its `children` the intersection of all
// their children types, and once a dependency augments `JSX.IntrinsicElements` with a custom
// element (`@descope/react-sdk` registers its web components there, and their children include
// `HTMLCollection`) a plain string no longer satisfies that intersection.
type TextElement = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'p' | 'span' | 'code';
const HINT_TO_ELEMENT: Record<string, TextElement> = {
  heading1: 'h1',
  heading2: 'h2',
  heading3: 'h3',
  heading4: 'h4',
  heading5: 'h5',
  body: 'p',
  caption: 'span',
  code: 'code',
};

/** A `unit`-typed property is stored as `{ value, unit }` (see `UnitValue` in types/schema). */
const isUnitValue = (v: unknown): v is { value: unknown; unit: unknown } =>
  !!v && typeof v === 'object' && 'value' in v && 'unit' in v;

/**
 * Total value -> text coercion shared by the display adapters. Ordered so the shapes a schema
 * property actually stores render as something a person can read, and only genuinely opaque
 * objects fall through to JSON.
 */
export const toDisplayText = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (isUnitValue(value)) {
    const magnitude = value.value === null || value.value === undefined ? '' : String(value.value);
    const unit = value.unit === null || value.unit === undefined ? '' : String(value.unit);
    return [magnitude, unit].filter(Boolean).join(' ');
  }
  return JSON.stringify(value);
};

export const TextAdapter: Adapter = ({ componentId, props, ctx }) => {
  // Coerced, not cast: `content` is `string` by contract, but this adapter is also the
  // type-default `view` renderer for whatever a schema property happens to hold. Rendering a
  // raw non-primitive as a React child throws "Objects are not valid as a React child" and
  // unmounts the entire surrounding property tree, so a wrong-shaped value must degrade to
  // text the way every other display adapter here does.
  const content = toDisplayText(ctx.resolveValue(props.content));
  const Tag_ = HINT_TO_ELEMENT[(props.usageHint as string) ?? 'body'] ?? 'p';
  return (
    <Tag_ data-component-id={componentId} className="display-adapter__text">
      {content}
    </Tag_>
  );
};

export const ImageAdapter: Adapter = ({ componentId, props, ctx }) => {
  const src = ctx.resolveValue(props.src) as string;
  const alt = props.alt !== undefined ? (ctx.resolveValue(props.alt) as string) : '';
  return (
    <img
      data-component-id={componentId}
      src={src}
      alt={alt}
      className="display-adapter__image"
      style={{ width: props.width as string | undefined, height: props.height as string | undefined }}
    />
  );
};

export const IconAdapter: Adapter = ({ componentId, props }) => (
  <i
    data-component-id={componentId}
    className={`pi pi-${props.name} display-adapter__icon`}
    style={{ fontSize: props.size as string | undefined }}
  />
);

export const DividerAdapter: Adapter = ({ componentId, props }) => (
  <div data-component-id={componentId}>
    <DividerAtom layout={(props.layout as 'horizontal' | 'vertical') ?? 'horizontal'} />
  </div>
);

const PROGRESS_TONE_MAP: Record<string, ProgressBarTone> = {
  primary: 'primary',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'info',
};

const PROGRESS_SIZE_MAP: Record<string, ProgressBarSize> = {
  small: 'small',
  normal: 'normal',
  large: 'large',
};

export const ProgressBarAdapter: Adapter = ({ componentId, props, ctx }) => {
  const { t } = useTranslation();
  const value = Math.max(0, Math.min(100, Number(ctx.resolveValue(props.value)) || 0));
  const label = props.label !== undefined ? (ctx.resolveValue(props.label) as string) : undefined;
  const tone = PROGRESS_TONE_MAP[(props.color as string) ?? 'primary'] ?? 'primary';
  const size = PROGRESS_SIZE_MAP[(props.size as string) ?? 'normal'] ?? 'normal';

  return (
    <div data-component-id={componentId}>
      {label && <div className="display-adapter__progress-label">{label}</div>}
      <ProgressBarAtom
        value={value}
        tone={tone}
        size={size}
        showValue={props.showValue !== false}
        ariaLabel={label || t('space.render.progressBarLabel', { value })}
      />
    </div>
  );
};

const BADGE_TONE_MAP: Record<string, TagTone> = {
  primary: 'accent',
  success: 'success',
  info: 'info',
  warning: 'warn',
  danger: 'danger',
};

export const BadgeAdapter: Adapter = ({ componentId, props, ctx }) => {
  const label = ctx.resolveValue(props.label) as string;
  const icon = props.icon ? `pi-${props.icon}` : undefined;
  const tone = BADGE_TONE_MAP[(props.severity as string) ?? 'primary'] ?? 'accent';
  return (
    <div data-component-id={componentId}>
      <TagAtom label={label} tone={tone} icon={icon} />
    </div>
  );
};

// ─── Renderer primitives (EAINT-855) ─────────────────────────────────────────
// Internal view-mode adapters `TYPE_DEFAULT_RENDERERS` names.

/**
 * Parses the two shapes a `date`/`datetime` property is actually persisted in, mirroring the
 * `parseDateTimeValue` this adapter replaced in `AdditionalPropertiesGrid`:
 *  - `{ date, time, timezone }` — the shape `DateTimePickerMolecule` writes. Its `timezone` is the
 *    zone the author *chose*, so it must be reported back, not the viewer's own.
 *  - an ISO string — no zone travels with it, so the viewer's zone is the only honest label.
 * Anything else yields an empty render rather than a wrong one.
 */
const parseDisplayDateTime = (value: unknown): { date: Date | null; time: string; timezone: string } => {
  const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const empty = { date: null, time: '', timezone: viewerZone };
  if (value === null || value === undefined || value === '') return empty;

  if (typeof value === 'object' && 'date' in value && 'time' in value && 'timezone' in value) {
    const stored = value as { date: unknown; time: unknown; timezone: unknown };
    const parsedStored = stored.date ? new Date(stored.date as string) : null;
    return {
      date: parsedStored && !Number.isNaN(parsedStored.getTime()) ? parsedStored : null,
      time: typeof stored.time === 'string' ? stored.time : '',
      timezone: (stored.timezone as string) || viewerZone,
    };
  }

  if (typeof value === 'string' || value instanceof Date) {
    const parsed = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(parsed.getTime())) return empty;
    // Local wall-clock, NOT `toISOString()` — the label reports the viewer's own zone, so the
    // time must be read in that same zone or every non-UTC viewer sees a shifted time under a
    // timezone name that contradicts it.
    return {
      date: parsed,
      time: `${String(parsed.getHours()).padStart(2, '0')}:${String(parsed.getMinutes()).padStart(2, '0')}`,
      timezone: viewerZone,
    };
  }

  return empty;
};

export const DateTimeDisplayAdapter: Adapter = ({ componentId, props, ctx }) => {
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : null;
  const { date, time, timezone } = parseDisplayDateTime(resolvedValue);

  return (
    <div data-component-id={componentId}>
      <DateTimeDisplayAtom
        date={date}
        time={time}
        timezone={timezone}
        format={(props.format as 'full' | 'short' | 'compact' | 'iso' | 'custom') ?? 'full'}
        customFormat={props.customFormat as string}
        showTimezone={props.showTimezone as boolean}
        showIso={props.showIso as boolean}
      />
    </div>
  );
};

export const ChipListDisplayAdapter: Adapter = ({ componentId, props, ctx }) => {
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : [];
  const items = Array.isArray(resolvedValue) ? resolvedValue : [];
  // Array-typed property values are plain strings/primitives, not objects — normalize to the
  // {label} shape ChipListDisplayConfig expects so this adapter covers the common TagListField
  // counterpart without requiring every array value to already be an object.
  const data = items.map((item) => (item && typeof item === 'object' ? item : { label: String(item) }));

  return (
    <div data-component-id={componentId}>
      <ChipListDisplayAtom data={data} config={{ labelField: 'label' }} compact={props.compact as boolean} />
    </div>
  );
};

export const GenericDisplayAdapter: Adapter = ({ componentId, props, ctx }) => {
  const resolvedValue = props.value !== undefined ? ctx.resolveValue(props.value) : undefined;
  const main = toDisplayText(resolvedValue);

  return (
    <div data-component-id={componentId}>
      <GenericDisplayAtom main={main} />
    </div>
  );
};
